// frontend/src/components/dialogs/ConfirmDialogShell.jsx

import Modal from '../Modal';
import { Save, X, AlertTriangle, CheckCircle2, Info, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * Reusable confirm dialog shell that standardizes the footer and theming
 * while allowing flexible body content via children prop
 */
const ConfirmDialogShell = ({
  // Modal props
  isOpen,
  onClose,
  title,
  size = 'md',
  children, // Body content

  // Footer/action props
  onConfirm,
  confirmLabel,
  cancelLabel,
  confirmVariant = 'primary', // 'primary', 'danger', 'warning', 'success'
  confirmIcon: ConfirmIcon = Save,
  cancelIcon: CancelIcon = X,
  isLoading = false,
  isDisabled = false,

  // Visual props
  showIcon = false,
  iconVariant = 'info', // 'info', 'warning', 'danger', 'success'

  // Additional footer customization
  footerClassName = '',
  ...modalProps
}) => {
  const { t } = useTranslation();
  const { components, cn } = useTheme();

  // Default labels with translation support
  const defaultConfirmLabel = confirmLabel || t('Confirm');
  const defaultCancelLabel = cancelLabel || t('Cancel');

  // Icon mapping for visual variants
  const iconMap = {
    info: Info,
    warning: AlertTriangle,
    danger: AlertCircle,
    success: CheckCircle2,
  };

  // Icon colors based on variant
  const iconColorMap = {
    info: 'text-blue-500',
    warning: 'text-amber-500',
    danger: 'text-red-500',
    success: 'text-emerald-500',
  };

  const IconComponent = iconMap[iconVariant];

  // Button variant mapping to theme classes
  const getButtonClasses = (variant) => {
    switch (variant) {
      case 'danger':
        return components.button.danger;
      case 'warning':
        return 'bg-amber-600 text-white hover:bg-amber-700 focus:ring-2 focus:ring-amber-500 focus:ring-offset-2';
      case 'success':
        return 'bg-emerald-600 text-white hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2';
      case 'secondary':
        return components.button.secondary;
      default:
        return components.button.primary;
    }
  };

  const confirmButtonClasses = cn(
    'px-4 py-2 rounded-md inline-flex items-center gap-2 font-medium text-sm',
    'transition-colors duration-200',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    getButtonClasses(confirmVariant)
  );

  const cancelButtonClasses = cn(
    'px-4 py-2 rounded-md inline-flex items-center gap-2 font-medium text-sm',
    'transition-colors duration-200',
    components.button.secondary
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size={size}
      footer={
        <div className={cn('flex justify-end gap-3', footerClassName)}>
          <button
            onClick={onConfirm}
            disabled={isLoading || isDisabled}
            className={confirmButtonClasses}
          >
            {isLoading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <ConfirmIcon className="w-4 h-4" />
            )}
            {defaultConfirmLabel}
          </button>
          <button
            onClick={onClose}
            disabled={isLoading}
            className={cancelButtonClasses}
          >
            <CancelIcon className="w-4 h-4" />
            {defaultCancelLabel}
          </button>
        </div>
      }
      {...modalProps}
    >
      <div className="space-y-4">
        {/* Optional icon display */}
        {showIcon && IconComponent && (
          <div className="flex justify-center">
            <IconComponent className={cn('w-12 h-12', iconColorMap[iconVariant])} />
          </div>
        )}

        {/* Body content */}
        {children}
      </div>
    </Modal>
  );
};

export default ConfirmDialogShell;